import { useMemo } from 'react';
import type { MapProviderType } from 'src/types/map-provider';
import { mapConfig } from 'src/config/map-config';
import { isUserInChina } from 'src/utils/country-detection';

/**
 * Hook to determine which map provider to use based on various factors
 * Implements Option 1: Environment default with fallbacks
 */
export function useMapProviderSelection(): {
  getProviderType: () => Promise<MapProviderType>;
  getProviderTypeSync: () => MapProviderType;
  isAutoMode: boolean;
} {
  const isAutoMode = mapConfig.defaultProvider === 'auto';

  
  const getProviderTypeSync = useMemo(() => {
    return (): MapProviderType => {
      
      const DEFAULT_PROVIDER = mapConfig.defaultProvider as MapProviderType;
      
      if (DEFAULT_PROVIDER === 'google' || DEFAULT_PROVIDER === 'baidu') {
        return DEFAULT_PROVIDER;
      }
      
      
      return 'google';
    };
  }, []);

  
  const getProviderType = useMemo(() => {
    return async (): Promise<MapProviderType> => {
      
      const DEFAULT_PROVIDER = mapConfig.defaultProvider;
      
      if (DEFAULT_PROVIDER === 'google' || DEFAULT_PROVIDER === 'baidu') {
        return DEFAULT_PROVIDER;
      }
      
      
      if (DEFAULT_PROVIDER === 'auto') {
        try {
          const inChina = await isUserInChina({
            timeout: mapConfig.countryDetection.timeout,
            fallbackToTimezone: mapConfig.countryDetection.useTimezone,
            fallbackToLanguage: mapConfig.countryDetection.useLanguage,
          });
          
          const selectedProvider: MapProviderType = inChina ? 'baidu' : 'google';
          return selectedProvider;
        } catch (error) {
          console.warn('Auto provider selection failed, falling back to Google:', error);
          return 'google';
        }
      }
      
      
      return 'google';
    };
  }, []);

  return {
    getProviderType,
    getProviderTypeSync,
    isAutoMode,
  };
}

/**
 * Simple hook for components that need immediate provider type
 * Uses environment configuration without async operations
 */
export function useMapProviderType(): MapProviderType {
  return useMemo(() => {
    
    const DEFAULT_PROVIDER = (process.env.NEXT_PUBLIC_DEFAULT_MAP_PROVIDER as MapProviderType) ?? 'google';
    
    if (DEFAULT_PROVIDER === 'google' || DEFAULT_PROVIDER === 'baidu') {
      return DEFAULT_PROVIDER;
    }
    
    
    return 'google';
  }, []);
}

/**
 * Hook for getting API keys based on provider selection
 */
export function useMapApiKeys(): Partial<Record<MapProviderType, string>> {
  return useMemo(() => {
    const keys: Partial<Record<MapProviderType, string>> = {};
    
    if (mapConfig.googleMapsApiKey) {
      keys.google = mapConfig.googleMapsApiKey;
    }
    
    if (mapConfig.baiduMapsApiKey) {
      keys.baidu = mapConfig.baiduMapsApiKey;
    }
    
    return keys;
  }, []);
}


